import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CADENCE_TYPE,
  type Product,
  type ProductsSelfData,
  type SubscriptionData,
} from '../../interfaces/subscriptions';
import { productsSelfDataQuery } from '../../queries/productsSelfDataQuery';

/* ------------------------------------------------------------------------------------------------
 * Interfaces / Types
 * ----------------------------------------------------------------------------------------------- */

type SelectedProducts = {
  [id: number]: boolean;
};

type GoWithBoxProductSubscription = Product & {
  variantId: number;
};
interface PostSwitchGwbSubscriptionsCadenceItem {
  variantId: number;
  switchToCadenceType: CADENCE_TYPE.NINETY_DAYS;
}

export type PostSwitchGwbSubscriptionCadenceBodyRequest = PostSwitchGwbSubscriptionsCadenceItem[];

interface KeepSubscriptionsContextReturn {
  data: ProductsSelfData | null;
  status: 'idle' | 'error' | 'loading' | 'success' | 'pending';
  refetch(): void;
  selectedProducts: SelectedProducts;
  goWithBoxProductSubscriptions: Product[];
  toggleProductSelection: (productId: number) => void;
  userHasKeptSubscriptions: boolean;
  setUserHasKeptSubscriptions: (value: boolean) => void;
  postSwitchGwbSubscriptionsCadenceBodyRequest: PostSwitchGwbSubscriptionCadenceBodyRequest;
  showKeepSubscriptionsPage: boolean;
  renderKeepSubscriptions(): void;
}

type ProductsSelectionToPostRequestProps = {
  goWithBoxProductSubscriptions: GoWithBoxProductSubscription[];
  selectedProducts: SelectedProducts;
};

/* ------------------------------------------------------------------------------------------------
 * Utilities
 * ----------------------------------------------------------------------------------------------- */

export const getGoWithBoxProductSubscriptions = (
  activeProductSubscriptions: SubscriptionData,
): GoWithBoxProductSubscription[] => {
  if (Object.keys(activeProductSubscriptions).length === 0) {
    return [];
  }

  const goWithBoxSubscriptions = activeProductSubscriptions?.GO_WITH_BOX?.subscriptions;

  if (goWithBoxSubscriptions) {
    return goWithBoxSubscriptions.map((subscription) => ({
      ...subscription.product,
      variantId: subscription.subscription.variantId,
    }));
  }

  return [];
};

const productsSelectionToPostRequest = ({
  goWithBoxProductSubscriptions,
  selectedProducts,
}: ProductsSelectionToPostRequestProps): PostSwitchGwbSubscriptionCadenceBodyRequest =>
  goWithBoxProductSubscriptions
    .filter((prod) => selectedProducts[prod.id] !== false)
    .map((prod) => ({
      variantId: prod.variantId,
      switchToCadenceType: CADENCE_TYPE.NINETY_DAYS,
    }));

/* ------------------------------------------------------------------------------------------------
 * Context and Provider
 * ----------------------------------------------------------------------------------------------- */

const KeepSubscriptionsContext = createContext<KeepSubscriptionsContextReturn>(null);

export const KeepSubscriptionsContextProvider = ({ children }: { children: ReactNode }) => {
  const { key, query } = productsSelfDataQuery;
  const { data, status, refetch } = useQuery<ProductsSelfData>({
    queryFn: query,
    queryKey: [key],
    retry: 2,
  });

  const [showKeepSubscriptionsPage, setShowKeepSubscriptionsPage] = useState(false);
  const [userHasKeptSubscriptions, setUserHasKeptSubscriptions] = useState(false);

  const renderKeepSubscriptions = () => setShowKeepSubscriptionsPage(true);

  const [selectedProducts, setSelectedProducts] = useState<SelectedProducts>({});

  const goWithBoxProductSubscriptions = useMemo(
    () =>
      data?.activeProductSubscriptions
        ? getGoWithBoxProductSubscriptions(data.activeProductSubscriptions)
        : [],
    [data],
  );

  const postSwitchGwbSubscriptionsCadenceBodyRequest = useMemo(
    () =>
      productsSelectionToPostRequest({
        goWithBoxProductSubscriptions,
        selectedProducts,
      }),
    [goWithBoxProductSubscriptions, selectedProducts, data],
  );

  const toggleProductSelection = (productId: number) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [productId]: prev[productId] === false,
    }));
  };

  return (
    <KeepSubscriptionsContext.Provider
      value={{
        showKeepSubscriptionsPage,
        renderKeepSubscriptions,
        data,
        status,
        refetch,
        selectedProducts,
        goWithBoxProductSubscriptions,
        toggleProductSelection,
        postSwitchGwbSubscriptionsCadenceBodyRequest,
        userHasKeptSubscriptions,
        setUserHasKeptSubscriptions,
      }}
    >
      {children}
    </KeepSubscriptionsContext.Provider>
  );
};

/* ------------------------------------------------------------------------------------------------
 * Consumer Context custom hook
 * ----------------------------------------------------------------------------------------------- */

export const useKeepSubscriptionsDataContext = () => {
  const context = useContext(KeepSubscriptionsContext);
  if (!context) {
    throw new Error(
      'You must use the KeepSubscriptionsContextProvider in order to consume this context.',
    );
  }
  return context;
};
